import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { servicePages } from '../data/servicePages';
import Reveal from '../components/Reveal';

/* ─── Count-up number ──────────────────────────────────────────────────── */
function StatNumber({ value, visible }) {
  const parsed = React.useMemo(() => {
    const m = value.match(/^(\d+\.?\d*)(.*)$/);
    if (!m) return null;
    return { num: parseFloat(m[1]), isFloat: m[1].includes('.'), suffix: m[2] };
  }, [value]);
  const [display, setDisplay] = useState(parsed ? ('0' + parsed.suffix) : value);
  useEffect(() => {
    if (!visible || !parsed) return;
    const { num, isFloat, suffix } = parsed;
    const steps = 60; const duration = 1400; let step = 0;
    const id = setInterval(() => {
      step++;
      if (step >= steps) { clearInterval(id); setDisplay(value); return; }
      const eased = 1 - Math.pow(1 - step / steps, 3);
      setDisplay((isFloat ? (num * eased).toFixed(1) : Math.floor(num * eased)) + suffix);
    }, duration / steps);
    return () => clearInterval(id);
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps
  return <>{display}</>;
}

/* ─── Styled image frame ───────────────────────────────────────────────── */
function FeatureImage({ src, alt, color }) {
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', inset: '-10%', borderRadius: '50%', background: `${color}0a`, filter: 'blur(70px)', pointerEvents: 'none' }} />
      <div style={{ position: 'relative', borderRadius: '1.25rem', overflow: 'hidden', border: `1px solid ${color}25`, boxShadow: `0 40px 80px -20px rgba(0,0,0,0.6), 0 0 60px ${color}12` }}>
        <img src={src} alt={alt} style={{ width: '100%', display: 'block', objectFit: 'cover', aspectRatio: '4/3' }} />
        <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${color}15 0%, transparent 60%)` }} />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
export default function ServicePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = servicePages[slug];
  const statsRef = useRef(null);
  const heroImgRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    if (!service) navigate('/');
    window.scrollTo(0, 0);
  }, [slug, service, navigate]);

  // Parallax on hero background
  useEffect(() => {
    const handle = () => {
      if (heroImgRef.current) {
        heroImgRef.current.style.transform = `translateY(${window.scrollY * 0.28}px) scale(1.08)`;
      }
    };
    window.addEventListener('scroll', handle, { passive: true });
    return () => window.removeEventListener('scroll', handle);
  }, []);

  // Stats count-up
  useEffect(() => {
    setStatsVisible(false);
    const el = statsRef.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setStatsVisible(true); ob.disconnect(); } },
      { threshold: 0.1 }
    );
    const t = setTimeout(() => ob.observe(el), 80);
    return () => { clearTimeout(t); ob.disconnect(); };
  }, [slug]);

  if (!service) return null;

  const c = service.color;
  const SI = (s, col) => `https://cdn.simpleicons.org/${s}/${col}`;
  const pad = 'clamp(1.5rem, 4vw, 4rem)';

  return (
    <div style={{ background: '#020617', color: '#fff', minHeight: '100vh' }}>

      {/* ══════════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════════ */}
      <section style={{ position: 'relative', minHeight: '90vh', display: 'flex', alignItems: 'flex-start', overflow: 'hidden' }}>

        {/* Parallax background */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <img
            ref={heroImgRef}
            src={service.heroImage}
            alt=""
            style={{ width: '100%', height: '108%', objectFit: 'cover', objectPosition: 'center', transformOrigin: 'center top', willChange: 'transform' }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(2,6,23,0.70)' }} />
          <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(105deg, rgba(2,6,23,0.95) 0%, rgba(2,6,23,0.60) 55%, rgba(2,6,23,0.25) 100%)` }} />
          <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to top, rgba(2,6,23,1) 0%, transparent 45%)` }} />
          <div style={{ position: 'absolute', top: '25%', left: '5%', width: '38rem', height: '38rem', borderRadius: '50%', background: `${c}08`, filter: 'blur(110px)', pointerEvents: 'none' }} />
        </div>

        {/* Subtle grid */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(to right, #1e293b 1px, transparent 1px), linear-gradient(to bottom, #1e293b 1px, transparent 1px)',
          backgroundSize: '4rem 4rem',
          maskImage: 'radial-gradient(ellipse 90% 80% at 50% 50%, #000 40%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 80% at 50% 50%, #000 40%, transparent 100%)',
          opacity: 0.3,
        }} />

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 10, width: '100%', padding: `5.5rem ${pad} 4rem` }}>

          {/* Centered block */}
          <div style={{ maxWidth: '56rem', width: '100%', margin: '0 auto', textAlign: 'center' }}>

            <div style={{ display: 'inline-flex', alignItems: 'center', padding: '0.3rem 1rem', borderRadius: '9999px', border: `1px solid ${c}40`, background: `${c}12`, marginBottom: '2.25rem' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: c, letterSpacing: '0.07em', textTransform: 'uppercase' }}>Codinity · Service</span>
            </div>

            <h1 style={{ fontSize: 'clamp(2.75rem, 6vw, 4.5rem)', fontWeight: 800, lineHeight: 1.06, color: '#fff', marginBottom: '1.25rem', letterSpacing: '-0.025em' }}>
              {service.title}
            </h1>
            <p style={{ fontSize: 'clamp(1.1rem, 2vw, 1.35rem)', fontWeight: 500, color: c, marginBottom: '1.25rem', lineHeight: 1.5, opacity: 0.92 }}>
              {service.tagline}
            </p>
            <p style={{ fontSize: '1.05rem', color: 'rgba(148,163,184,0.9)', lineHeight: 1.8, maxWidth: '38rem', margin: '0 auto 2.5rem' }}>
              {service.description}
            </p>

            <div style={{ display: 'flex', gap: '0.875rem', flexWrap: 'wrap', justifyContent: 'center' }}>
              <Link to="/contact" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.95rem 2rem', background: c, color: '#fff', borderRadius: '0.5rem', fontWeight: 700, textDecoration: 'none', fontSize: '0.95rem', boxShadow: `0 8px 32px ${c}40`, transition: 'opacity 0.2s, transform 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'none'; }}>
                Start a Project <ArrowRight style={{ width: '1rem', height: '1rem' }} />
              </Link>
              <Link to="/services" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.95rem 2rem', border: '1.5px solid rgba(51,65,85,0.9)', color: '#94a3b8', borderRadius: '0.5rem', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem', transition: 'border-color 0.2s, color 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(51,65,85,0.9)'; e.currentTarget.style.color = '#94a3b8'; }}>
                All Services
              </Link>
            </div>

            {/* Stats */}
            <div ref={statsRef} className="service-stats-grid">
              {service.stats.map((s, i) => (
                <div key={i} className="service-stat-item">
                  <div style={{ fontSize: 'clamp(2rem, 5vw, 3.25rem)', fontWeight: 800, color: c, lineHeight: 1, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>
                    <StatNumber value={s.value} visible={statsVisible} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 500, marginTop: '0.5rem', whiteSpace: 'nowrap' }}>{s.label}</div>
                </div>
              ))}
            </div>

          </div>
        </div>

        {/* Scroll indicator */}
        <div style={{ position: 'absolute', bottom: '2rem', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem', opacity: 0.4 }}>
          <div style={{ width: '1px', height: '3rem', background: `linear-gradient(to bottom, transparent, ${c})` }} />
          <div style={{ width: '0.4rem', height: '0.4rem', borderRadius: '50%', background: c }} />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          FEATURES — alternating image + text rows
      ══════════════════════════════════════════════════ */}
      <section style={{ padding: '7rem 0', background: '#020617' }}>
        <div style={{ maxWidth: 'min(100rem, calc(100% - clamp(1.5rem, 5vw, 6rem)))', margin: '0 auto', padding: `0 ${pad}` }}>

          <Reveal direction="up">
            <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: c, letterSpacing: '0.1em', textTransform: 'uppercase' }}>What We Deliver</span>
              <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.6rem)', fontWeight: 700, color: '#fff', marginTop: '0.75rem', letterSpacing: '-0.02em' }}>
                Why teams choose us
              </h2>
            </div>
          </Reveal>

          {service.features.map((feature, i) => {
            const isEven = i % 2 === 0;
            return (
              <div key={i} className="feature-split-row" style={{ marginBottom: i < service.features.length - 1 ? '7rem' : 0 }}>

                {/* Text side */}
                <Reveal direction={isEven ? 'left' : 'right'} delay={0}>
                  <div style={{ order: isEven ? 0 : 1 }} className="feature-text-col">
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '3.5rem', height: '3.5rem', borderRadius: '1rem', background: `${c}15`, color: c, border: `1px solid ${c}25`, marginBottom: '1.5rem' }}>
                      {feature.icon}
                    </div>
                    <h3 style={{ fontSize: 'clamp(1.4rem, 2.5vw, 1.9rem)', fontWeight: 700, color: '#f1f5f9', marginBottom: '1rem', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                      {feature.title}
                    </h3>
                    <p style={{ fontSize: '1.05rem', color: '#64748b', lineHeight: 1.85, maxWidth: '26rem' }}>
                      {feature.description}
                    </p>
                    <div style={{ marginTop: '2rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: c, fontSize: '0.88rem', fontWeight: 600 }}>
                      <span>Learn more</span>
                      <ArrowRight style={{ width: '0.9rem', height: '0.9rem' }} />
                    </div>
                  </div>
                </Reveal>

                {/* Image side */}
                <Reveal direction={isEven ? 'right' : 'left'} delay={120}>
                  <div style={{ order: isEven ? 1 : 0 }}>
                    <FeatureImage src={feature.image} alt={feature.title} color={c} />
                  </div>
                </Reveal>

              </div>
            );
          })}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          PROCESS — vertical animated timeline
      ══════════════════════════════════════════════════ */}
      <section style={{ padding: '7rem 0', background: 'rgba(15,23,42,0.4)' }}>
        <div style={{ maxWidth: 'min(100rem, calc(100% - clamp(1.5rem, 5vw, 6rem)))', margin: '0 auto', padding: `0 ${pad}` }}>

          <Reveal direction="up">
            <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: c, letterSpacing: '0.1em', textTransform: 'uppercase' }}>How We Work</span>
              <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.6rem)', fontWeight: 700, color: '#fff', marginTop: '0.75rem', letterSpacing: '-0.02em' }}>
                Our Process
              </h2>
            </div>
          </Reveal>

          <div style={{ maxWidth: '52rem', margin: '0 auto' }}>
            {service.process.map((step, i) => (
              <Reveal key={i} direction="right" delay={i * 80}>
                <div style={{ display: 'flex', gap: '2rem', position: 'relative' }}>

                  {/* Left rail */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: '3rem' }}>
                    <div style={{ width: '3rem', height: '3rem', borderRadius: '50%', background: `${c}15`, border: `2px solid ${c}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 0 20px ${c}30` }}>
                      <span style={{ fontSize: '0.7rem', fontWeight: 800, color: c }}>{step.step}</span>
                    </div>
                    {i < service.process.length - 1 && (
                      <div style={{ width: '2px', flexGrow: 1, minHeight: '3rem', background: `linear-gradient(to bottom, ${c}60, ${c}10)`, marginTop: '0.4rem' }} />
                    )}
                  </div>

                  {/* Content */}
                  <div style={{ paddingBottom: i < service.process.length - 1 ? '3rem' : 0, paddingTop: '0.5rem' }}>
                    <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '0.6rem' }}>{step.title}</h3>
                    <p style={{ fontSize: '0.92rem', color: '#64748b', lineHeight: 1.75, maxWidth: '36rem' }}>{step.description}</p>
                  </div>

                </div>
              </Reveal>
            ))}
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          TECH STACK — staggered cards
      ══════════════════════════════════════════════════ */}
      <section style={{ padding: '7rem 0', background: '#020617' }}>
        <div style={{ maxWidth: 'min(100rem, calc(100% - clamp(1.5rem, 5vw, 6rem)))', margin: '0 auto', padding: `0 ${pad}` }}>

          <Reveal direction="up">
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '3rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: c, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Technologies</span>
                <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.6rem)', fontWeight: 700, color: '#fff', marginTop: '0.75rem', letterSpacing: '-0.02em' }}>Our Stack</h2>
              </div>
              <p style={{ fontSize: '0.85rem', color: '#64748b', maxWidth: '22rem', textAlign: 'right', lineHeight: 1.6 }}>
                Production-tested tools we use to deliver reliable, scalable work.
              </p>
            </div>
          </Reveal>

          <div className="tech-grid">
            {service.tech.map((tech, i) => (
              <Reveal key={tech.name} direction="up" delay={i * 60}>
                <div
                  style={{ background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(30,41,59,0.9)', borderRadius: '1rem', padding: '1.5rem 1rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.875rem', cursor: 'default', transition: 'border-color 0.2s, transform 0.2s, box-shadow 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = `${c}45`; e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = `0 16px 40px rgba(0,0,0,0.35), 0 0 0 1px ${c}20`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(30,41,59,0.9)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ width: '3rem', height: '3rem', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(30,41,59,0.6)', borderRadius: '0.75rem', padding: '0.5rem' }}>
                    {/* slug === null: cdn.simpleicons.org no longer serves an icon for this
                        brand, so go straight to the letter tile instead of a doomed request. */}
                    {tech.slug ? (
                      <img src={SI(tech.slug, tech.color)} alt={tech.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        onError={e => { e.currentTarget.parentElement.innerHTML = `<span style="font-size:1.4rem;font-weight:800;color:${c};font-family:monospace">${tech.name[0]}</span>`; }} />
                    ) : (
                      <span style={{ fontSize: '1.4rem', fontWeight: 800, color: c, fontFamily: 'monospace' }}>{tech.name[0]}</span>
                    )}
                  </div>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#94a3b8', lineHeight: 1.3 }}>{tech.name}</span>
                </div>
              </Reveal>
            ))}
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          CTA BANNER
      ══════════════════════════════════════════════════ */}
      <section style={{ padding: '7rem 0', background: 'rgba(15,23,42,0.4)' }}>
        <div style={{ maxWidth: 'min(100rem, calc(100% - clamp(1.5rem, 5vw, 6rem)))', margin: '0 auto', padding: `0 ${pad}` }}>
          <Reveal direction="up">
            <div style={{ position: 'relative', borderRadius: '1.5rem', overflow: 'hidden', minHeight: '22rem', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <div style={{ position: 'absolute', inset: 0 }}>
                <img src={service.heroImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.25) saturate(0.6)' }} />
                <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, rgba(2,6,23,0.85) 0%, ${c}18 100%)` }} />
              </div>
              <div style={{ position: 'relative', zIndex: 1, padding: '4rem 2rem', maxWidth: '38rem' }}>
                <div style={{ display: 'inline-flex', padding: '0.25rem 0.875rem', borderRadius: '9999px', border: `1px solid ${c}35`, background: `${c}12`, marginBottom: '1.25rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: c, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{service.title}</span>
                </div>
                <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', fontWeight: 800, color: '#fff', marginBottom: '1rem', lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                  Ready to build?
                </h2>
                <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.6)', marginBottom: '2rem', lineHeight: 1.75 }}>
                  Free consultation. Honest timelines. No fluff.
                </p>
                <Link to="/contact" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.625rem', padding: '1rem 2.25rem', background: c, color: '#fff', borderRadius: '0.625rem', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', boxShadow: `0 8px 30px ${c}40`, transition: 'opacity 0.2s, transform 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'none'; }}>
                  Start a Conversation <ArrowRight style={{ width: '1.1rem', height: '1.1rem' }} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

    </div>
  );
}
